import PropTypes from 'prop-types';
import { useState } from 'react';
// @mui
import { Box, Chip, Paper, Button, Stack, Divider } from '@mui/material';
// components
import { Iconify } from '..';
// style
import { tableFilterBox, filterCountChipStyle } from '../../styles/NewLead';

const MAX_VISIBLE_GROUPS = 3;

const getActiveFilterCount = (filters) => {
    if (!filters) return 0;
    let count = 0;
    if (filters.status?.length) count += 1;
    if (filters.treatment?.length) count += 1;
    if (filters.tags?.length) count += 1;
    if (filters.referral?.length) count += 1;
    if (filters.assignTo?.length) count += 1;
    if (filters.consultationBookedWith?.length) count += 1;
    if (filters.startDate?.length && filters.endDate?.length) count += 1;
    if (filters.depositPaidFilter?.length) count += 1;
    if (filters.assigneeSelectedForFilter?.length) count += 1;
    if (filters.archiveReason?.length) count += 1;
    return count;
};

const TableFilterResult = ({
    filters,
    onResetFilters,
    onReset,
    handleTreatment,
    handleReferral,
    dateChange,
    handleTags,
    handlePractitioner,
    handleDepositPendingFilter,
    handleConsultationBookedWith,
    tableTitle,
    handleAssigneeFilter,
    hideNewFilters,
    isTreatmentStarted,
    handleArchiveFilter,
    selectedCount,
    ...other
}) => {
    const [showAll, setShowAll] = useState(false);
    const activeCount = getActiveFilterCount(filters);

    const handleRemoveTreatment = (inputValue) => {
        const newValue = filters.treatment.filter((item) => item.id !== inputValue);
        handleTreatment('', newValue);
    };

    const handleRemoveTags = (id) => {
        const newValue = filters.tags.filter((item) => item?.id !== id);
        const tagName = newValue.map((item) => item?.name);
        handleTags(newValue, tagName);
    };

    const handleRemoveAssignee = (id) => {
        const newValue = filters.assigneeSelectedForFilter?.filter((res) => res.id !== id)
        handleAssigneeFilter('', newValue);
    };

    const handleRemoveBookedWith = (id) => {
        const newValue = filters.assignTo?.filter((res) => res.id !== id)
        handlePractitioner('', newValue);
    };
    const handleRemoveConsultationBookedWith = (id) => {
        const newValue = filters.consultationBookedWith?.filter((res) => res.id !== id)
        handleConsultationBookedWith('', newValue);
    };

    const filterGroups = [];

    if (filters?.status?.length && tableTitle !== 'Aligner Approve Leads' && !hideNewFilters) {
        filterGroups.push(
            <Block key='status' label={(filters.statusLabel?.length > 0 && `${filters.statusLabel}:`) || 'Status:'}>
                <Chip label={filters?.status} size='small' variant='filled' sx={blockChipSx} />
            </Block>
        );
    }

    if (filters?.treatment?.length && tableTitle !== 'Aligner Approve Leads') {
        filterGroups.push(
            <Block key='treatment' label='Treatments:'>
                {filters?.treatment?.map((item) => (
                    <Chip key={item.id} label={item.name} size='small' variant='filled' sx={blockChipSx} onDelete={() => handleRemoveTreatment(item.id)} />
                ))}
            </Block>
        );
    }

    if (filters?.tags?.length) {
        filterGroups.push(
            <Block key='tags' label='Tags:'>
                {filters?.tags?.map((item) => (
                    <Chip key={item?.id} label={item?.name} size='small' variant='filled' sx={blockChipSx} onDelete={() => handleRemoveTags(item?.id)} />
                ))}
            </Block>
        );
    }

    if (filters?.referral?.length) {
        filterGroups.push(
            <Block key='referral' label='Referral:'>
                <Chip label={filters?.referral} size='small' variant='filled' sx={blockChipSx} onDelete={(e) => handleReferral(e, { value: '' })} />
            </Block>
        );
    }

    if (filters?.assignTo?.length) {
        filterGroups.push(
            <Block key='assignTo' label={`${isTreatmentStarted ? "Treatment booked with:" : "Booked with:"} `}>
                {filters?.assignTo?.map((item) => (
                    <Chip key={item.id} label={item.name} size='small' variant='filled' sx={blockChipSx} onDelete={() => handleRemoveBookedWith(item.id)} />
                ))}
            </Block>
        );
    }

    if (filters?.consultationBookedWith?.length) {
        filterGroups.push(
            <Block key='consultationBookedWith' label='Consultation Booked with:'>
                {filters?.consultationBookedWith?.map((item) => (
                    <Chip key={item.id} label={item.name} size='small' variant='filled' sx={blockChipSx} onDelete={() => handleRemoveConsultationBookedWith(item.id)} />
                ))}
            </Block>
        );
    }

    if (filters?.startDate?.length && filters?.endDate?.length) {
        filterGroups.push(
            <Block key='date' label='Date filter:'>
                <Chip label={`${filters?.startDate} to ${filters?.endDate}`} size='small' variant='filled' sx={blockChipSx} onDelete={() => dateChange([null, null])} />
            </Block>
        );
    }

    if (filters?.depositPaidFilter?.length) {
        filterGroups.push(
            <Block key='deposit' label='Deposit paid filter:'>
                <Chip label={`${filters?.depositPaidFilter}` || ''} size='small' variant='filled' sx={blockChipSx} onDelete={() => handleDepositPendingFilter('')} />
            </Block>
        );
    }

    if (filters?.assigneeSelectedForFilter?.length) {
        filterGroups.push(
            <Block key='assignee' label='Assignee:'>
                {filters?.assigneeSelectedForFilter?.map((item) => (
                    <Chip key={item.id} label={item.name} size='small' variant='filled' sx={blockChipSx} onDelete={() => handleRemoveAssignee(item.id)} />
                ))}
            </Block>
        );
    }

    if (filters?.archiveReason?.length) {
        filterGroups.push(
            <Block key='archiveReason' label='Archive reason:'>
                <Chip label={filters?.archiveReason} size='small' variant='filled' sx={blockChipSx} onDelete={(e) => handleArchiveFilter(e, { value: '' })} />
            </Block>
        );
    }

    if (selectedCount && tableTitle === 'All Patients') {
        filterGroups.push(
            <Block key='selected' label='Selected:'>
                <Chip label={`${selectedCount}`} size='small' variant='filled' sx={blockChipSx} />
            </Block>
        );
    }

    const shouldCollapse = filterGroups.length > MAX_VISIBLE_GROUPS;
    const visibleGroups = shouldCollapse && !showAll ? filterGroups.slice(0, MAX_VISIBLE_GROUPS) : filterGroups;

    return (
        <Stack spacing={1.5} {...other} sx={{width:"100%", marginTop:"10px"}}>
            <Stack flexGrow={1} spacing={1} direction='row' flexWrap='wrap' alignItems='center' justifyContent='space-between'>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap='wrap'>
                    {activeCount > 0 && (
                        <>
                            <Chip
                                label={`${activeCount} active ${activeCount === 1 ? 'filter' : 'filters'}`}
                                color='primary'
                                variant='outlined'
                                size='small'
                                sx={filterCountChipStyle}
                            />
                            <Divider orientation='vertical' flexItem sx={{ mx: 0.5 }} />
                        </>
                    )}

                    {visibleGroups}

                    {shouldCollapse && (
                        <Button
                            size='small'
                            variant='text'
                            onClick={() => setShowAll(!showAll)}
                            sx={{ minWidth: 'auto', px: 1 }}
                        >
                            {showAll ? 'Show less' : `+${filterGroups.length - MAX_VISIBLE_GROUPS} more`}
                        </Button>
                    )}

                    {onReset && (
                        <Button
                            variant='outlined'
                            color='error'
                            size='small'
                            onClick={onResetFilters}
                            startIcon={<Iconify icon='solar:trash-bin-trash-bold' />}
                            sx={{ ml: 1 }}
                        >
                            Clear all
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
};

TableFilterResult.propTypes = {
    filters: PropTypes.object,
    onResetFilters: PropTypes.func,
    results: PropTypes.number,
    onReset: PropTypes.bool,
    handleTreatment: PropTypes.func,
    handleReferral: PropTypes.func,
    dateChange: PropTypes.func,
    handleTags: PropTypes.func,
    handlePractitioner: PropTypes.func,
    handleDepositPendingFilter: PropTypes.func,
    handleConsultationBookedWith: PropTypes.func,
    tableTitle: PropTypes.string,
    handleAssigneeFilter: PropTypes.func,
    hideNewFilters: PropTypes.bool,
    isTreatmentStarted: PropTypes.bool,
    handleArchiveFilter: PropTypes.func,
    selectedCount: PropTypes.number
};

const blockChipSx = {
    bgcolor: 'rgba(145, 158, 171, 0.12)',
    color: 'text.primary',
    '& .MuiChip-deleteIcon': {
        color: 'rgba(145, 158, 171, 0.5)',
        '&:hover': {
            color: 'rgba(145, 158, 171, 0.7)',
        },
    },
};

const Block = ({ label, children, sx, ...other }) => (
    <Stack
        component={Paper}
        variant='outlined'
        spacing={1}
        direction='row'
        sx={tableFilterBox(sx)}
        {...other}
    >
        <Box component='span' sx={{ typography: 'subtitle2', color: 'text.secondary', fontSize: '12px', whiteSpace: 'nowrap' }}>
            {label}
        </Box>

        <Stack spacing={1} direction='row' flexWrap='wrap'>
            {children}
        </Stack>
    </Stack>
);

Block.propTypes = {
    children: PropTypes.node,
    label: PropTypes.string,
    sx: PropTypes.object,
    hideNewFilters: PropTypes.bool
};

export default TableFilterResult;
